import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { parametrization, type ModalityDetail } from '@/shared/api/resources';
import { Badge } from '@/shared/ui/Badge';
import {
  DetailCard,
  DetailError,
  DetailLayout,
  DetailLoading,
  DetailTabs,
} from '@/shared/ui/DetailLayout';
import { HistoryTab } from '@/shared/ui/HistoryTab';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import { CoberturasAdicionais } from './CoberturasAdicionais';
import { CondicoesParticulares } from './CondicoesParticulares';
import { DetalhesDaModalidade } from './DetalhesDaModalidade';
import { DocumentosDaModalidade } from './DocumentosDaModalidade';
import { ModalidadesComplementares } from './ModalidadesComplementares';
import { rotuloDaAcao, rotuloDoCampo } from './rotulos';
import { TextoDaModalidade } from './TextoDaModalidade';
import { Trilha } from './Trilha';

type Aba =
  | 'detalhes'
  | 'objeto'
  | 'condicao-geral'
  | 'condicoes-especiais'
  | 'condicoes-particulares'
  | 'documentos'
  | 'criterios';

/** As sete abas de "Modalidade", na ordem do OnPoint. */
const abas: { key: Aba; title: string }[] = [
  { key: 'detalhes', title: 'Detalhes da modalidade' },
  { key: 'objeto', title: 'Objeto' },
  { key: 'condicao-geral', title: 'Condicao Geral' },
  { key: 'condicoes-especiais', title: 'Condicoes Especiais' },
  { key: 'condicoes-particulares', title: 'Condicoes Particulares' },
  { key: 'documentos', title: 'Documentos' },
  { key: 'criterios', title: 'Criterios para Enquadramento de Riscos' },
];

/** As abas de texto versionado, com o rotulo do editor e o botao do OnPoint. */
function AbaDeTexto({ aba, modalityId }: { aba: Aba; modalityId: string }) {
  switch (aba) {
    case 'objeto':
      return (
        <TextoDaModalidade
          key="objeto"
          referencia={{ family: 'objeto', modalityId }}
          rotuloDoEditor="Objeto"
          ajudaDoEditor="Texto-base do objeto da modalidade, com as TAGs que a cotacao preenche. Saneado de novo no servidor ao salvar; o texto da seguradora e carregado depois."
          rotuloDeSalvar="Salvar objeto"
          comTags
        />
      );
    case 'condicao-geral':
      return (
        <TextoDaModalidade
          key="condicao-geral"
          referencia={{ family: 'condicao', modalityId, kind: 'condicao_geral' }}
          rotuloDoEditor="Condicao Geral"
          ajudaDoEditor="Condicoes gerais da modalidade, ligadas ao processo SUSEP. Saneadas de novo no servidor ao salvar."
          rotuloDeSalvar="Salvar condicoes gerais"
        />
      );
    case 'condicoes-especiais':
      return (
        <TextoDaModalidade
          key="condicoes-especiais"
          referencia={{ family: 'condicao', modalityId, kind: 'condicoes_especiais' }}
          rotuloDoEditor="Condicoes Especiais"
          ajudaDoEditor="Condicoes especiais da modalidade. Saneadas de novo no servidor ao salvar."
          rotuloDeSalvar="Salvar condicoes especiais"
        />
      );
    case 'criterios':
      return (
        <TextoDaModalidade
          key="criterios"
          referencia={{
            family: 'condicao',
            modalityId,
            kind: 'criterios_enquadramento',
          }}
          rotuloDoEditor="Criterios para Enquadramento de Riscos"
          ajudaDoEditor="Criterios para enquadramento de riscos da modalidade (SUB-017). O texto e guardado; nenhuma regra o le."
          rotuloDeSalvar="Salvar criterios"
        />
      );
    default:
      return null;
  }
}

function SecaoModalidade({ detalhe }: { detalhe: ModalityDetail }) {
  const [aba, setAba] = useState<Aba>('detalhes');
  return (
    <DetailCard title="Modalidade">
      <DetailTabs
        tabs={abas}
        active={aba}
        onChange={setAba}
        label="Abas da modalidade"
      />
      {aba === 'detalhes' ? (
        // A chave remonta o formulario quando a versao muda, para que os campos
        // partam do que esta gravado depois de salvar ou ativar.
        <DetalhesDaModalidade
          key={`${detalhe.updated_at ?? ''}-${detalhe.draft_setting?.updated_at ?? ''}-${detalhe.active_setting?.id ?? ''}`}
          detalhe={detalhe}
        />
      ) : aba === 'condicoes-particulares' ? (
        <CondicoesParticulares modalityId={detalhe.id} />
      ) : aba === 'documentos' ? (
        <DocumentosDaModalidade modalityId={detalhe.id} />
      ) : (
        <AbaDeTexto aba={aba} modalityId={detalhe.id} />
      )}
    </DetailCard>
  );
}

/**
 * Configuracao da modalidade (`/parametrizacao/modalidades/:modalityId`), a tela
 * "Editar configuracao" do OnPoint.
 *
 * O menu lateral tem Modalidade, Coberturas Adicionais, Modalidades Complementares e
 * Historico de Alteracoes; "Modalidade" tem as sete abas do OnPoint, nesta ordem.
 * "Detalhes da modalidade" e o historico sao do P-PAR-1; as abas de texto, as
 * condicoes particulares, os documentos, as coberturas adicionais e as
 * complementares, do P-PAR-2.
 */
export function ModalityDetailPage() {
  const { modalityId = '' } = useParams();
  const navigate = useNavigate();

  const consulta = useQuery({
    queryKey: ['modality', modalityId],
    queryFn: ({ signal }) => parametrization.getModality(modalityId, signal),
    enabled: modalityId !== '',
  });

  if (consulta.isPending) return <DetailLoading />;
  if (consulta.isError) {
    return (
      <DetailError
        title="Modalidade"
        alertTitle="Nao foi possivel abrir a modalidade"
        message={consulta.error.message}
        onBack={() => void navigate('/parametrizacao/modalidades')}
      />
    );
  }

  const detalhe = consulta.data;

  return (
    <div className="flex flex-col gap-3">
      <Trilha
        itens={[
          { rotulo: 'Parametrizacao', para: '/parametrizacao' },
          { rotulo: 'Lista de modalidades', para: '/parametrizacao/modalidades' },
        ]}
        atual={detalhe.name}
      />
      <DetailLayout
        navLabel="Secoes da modalidade"
        title="Editar configuracao"
        description={`${detalhe.name} . ${detalhe.product.name}${
          detalhe.modality_group ? ` . ${detalhe.modality_group.name}` : ' . Sem grupo'
        }`}
        meta={
          <>
            <StatusBadge kind="cadastro" value={detalhe.status} />
            {detalhe.active_setting ? (
              <Badge tone="success">
                Versao {detalhe.active_setting.version} em vigor
              </Badge>
            ) : (
              <Badge tone="neutral">Sem versao em vigor</Badge>
            )}
            {detalhe.draft_setting ? (
              <Badge tone="warning">Rascunho {detalhe.draft_setting.version}</Badge>
            ) : null}
          </>
        }
        actions={
          <Link
            to="/parametrizacao/modalidades"
            className="inline-flex h-10 items-center rounded-md px-3 text-sm font-medium text-ink-600 transition-colors duration-(--duration-fast) hover:bg-ink-100"
          >
            Voltar para a lista
          </Link>
        }
        sections={[
          {
            key: 'modalidade',
            title: 'Modalidade',
            render: () => <SecaoModalidade detalhe={detalhe} />,
          },
          {
            key: 'coberturas',
            title: 'Coberturas Adicionais',
            render: () => <CoberturasAdicionais modalityId={detalhe.id} />,
          },
          {
            key: 'complementares',
            title: 'Modalidades Complementares',
            render: () => <ModalidadesComplementares detalhe={detalhe} />,
          },
          {
            key: 'historico',
            title: 'Historico de Alteracoes',
            render: () => (
              <HistoryTab
                entityType="modality"
                entityId={detalhe.id}
                description="Toda mudanca no cadastro e nas versoes da configuracao, com o antes e o depois."
                actionLabel={rotuloDaAcao}
                fieldLabel={rotuloDoCampo}
              />
            ),
          },
        ]}
      />
    </div>
  );
}
